import { AlertTriangleIcon, HomeIcon, RefreshCwIcon } from 'lucide-react';

import React, { Component, ErrorInfo, ReactNode } from 'react';

import { Button, Card } from '@splitfiesta/ui';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log to error tracking service (e.g., Sentry)
    interface WindowWithSentry extends Window {
      Sentry?: {
        captureException: (
          error: Error,
          context?: {
            contexts?: {
              react?: {
                componentStack?: string;
              };
            };
          }
        ) => void;
      };
    }

    if (typeof window !== 'undefined') {
      const windowWithSentry = window as WindowWithSentry;
      if (windowWithSentry.Sentry) {
        windowWithSentry.Sentry.captureException(error, {
          contexts: {
            react: {
              componentStack: errorInfo.componentStack || undefined,
            },
          },
        });
      }
    }
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/groups';
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-canvas dark:bg-canvas-dark px-4">
          <div className="max-w-md w-full">
            <Card className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-error-100 rounded-full mb-4">
                <AlertTriangleIcon className="w-6 h-6 text-error-600" />
              </div>

              <h1 className="text-xl font-semibold text-center text-gray-900 mb-2">
                Oops! Something went wrong
              </h1>

              <p className="text-sm text-gray-600 text-center mb-6">
                We encountered an unexpected error. The issue has been logged
                and we&apos;ll look into it.
              </p>

              {import.meta.env.DEV && this.state.error && (
                <div className="mb-6 p-4 bg-gray-100 rounded-lg">
                  <p className="text-xs font-mono text-gray-700 mb-2">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <details className="text-xs text-gray-600">
                      <summary className="cursor-pointer hover:text-gray-800">
                        Component Stack
                      </summary>
                      <pre className="mt-2 whitespace-pre-wrap">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <Button
                  onClick={this.handleReset}
                  variant="primary"
                  fullWidth
                  leftIcon={<RefreshCwIcon className="w-4 h-4" />}
                >
                  Try Again
                </Button>

                <Button
                  onClick={this.handleReload}
                  variant="outline"
                  fullWidth
                  leftIcon={<RefreshCwIcon className="w-4 h-4" />}
                >
                  Reload Page
                </Button>

                <Button
                  onClick={this.handleGoHome}
                  variant="outline"
                  fullWidth
                  leftIcon={<HomeIcon className="w-4 h-4" />}
                >
                  Go to Home
                </Button>
              </div>
            </Card>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
